import { createAiContextDataSnapshot } from './normalize-ai-context-data';
import type { AiContextEntry, AiContextPriority, SerializeContextOptions, SerializedAiContext } from './types';

const DEFAULT_MAX_TOKEN_BUDGET = 4000;

const priorityOrder: Readonly<Record<AiContextPriority, number>> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
};

const estimateTokens = (serializedData: string): number => {
    return Math.ceil(serializedData.length / 4);
};

export const serializeContext = (
    entries: ReadonlyMap<string, AiContextEntry>,
    options?: SerializeContextOptions,
): SerializedAiContext => {
    const maxBudget = options?.maxTokenBudget ?? DEFAULT_MAX_TOKEN_BUDGET;
    const categoryFilter = options?.categories;

    const sortedEntries = [...entries.values()]
        .flatMap((entry) => {
            const dataSnapshot = createAiContextDataSnapshot(entry.data);

            if (dataSnapshot === null) {
                return [];
            }

            return [
                { entry: { ...entry, data: dataSnapshot.normalizedData }, serializedData: dataSnapshot.serializedData },
            ];
        })
        .filter(({ entry }) => (categoryFilter ? categoryFilter.includes(entry.category) : true))
        .sort((a, b) => {
            const priorityDiff = priorityOrder[a.entry.priority] - priorityOrder[b.entry.priority];

            if (priorityDiff !== 0) {
                return priorityDiff;
            }

            return b.entry.registeredAt - a.entry.registeredAt;
        });

    const includedEntries: AiContextEntry[] = [];
    let usedTokens = 0;

    for (const { entry, serializedData } of sortedEntries) {
        const tokenCost = estimateTokens(serializedData);

        if (entry.priority === 'critical') {
            includedEntries.push(entry);
            usedTokens += tokenCost;
            continue;
        }

        if (usedTokens + tokenCost <= maxBudget) {
            includedEntries.push(entry);
            usedTokens += tokenCost;
        }
    }

    return {
        collectedAt: Date.now(),
        entries: includedEntries.map((entry) => ({
            category: entry.category,
            data: entry.data,
            key: entry.key,
            priority: entry.priority,
        })),
        version: 1,
    };
};
